import type { AxiosInstance } from 'axios'

import type {
  CategoryMapping,
  CategoryMappingResponse,
  CategoryScan,
  FeedPreview,
  InventoryMapping,
} from '../models/product.types'
import { normalizePreview } from '../service/categoryMapping'

import { DEFINITIONS_REQUEST_CONFIG } from './config'

import { appHttp, unwrap, unwrapEnvelope, type ApiEnvelope } from '@/shared/http'

export interface FeedPreviewRequest {
  workspace_id: number | string
  raw_data_type: string
  merge_with?: string
}

interface InventorySnapshotRow {
  value?: InventoryMapping
}

interface SelectableKeyRow {
  value?: string | string[]
}

export interface ProductIdMappingStatus {
  selected_column?: string[] | null
  source?: 'auto' | 'manual' | null
  verdict?: 'not_needed' | null
}

export class ProductCustomizationApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  async getCategoryMapping(
    accountId: number | string,
    workspaceId: number | string,
  ): Promise<CategoryMappingResponse> {
    return (
      unwrap(
        await this.http.get<CategoryMappingResponse>(
          `/accounts/${accountId}/workspace/${workspaceId}/category_mapping`,
          DEFINITIONS_REQUEST_CONFIG,
        ),
      ) ?? []
    )
  }

  async saveCategoryMapping(
    accountId: number | string,
    workspaceId: number | string,
    mapping: CategoryMapping,
  ): Promise<unknown> {
    return unwrap(
      await this.http.post(
        `/accounts/${accountId}/workspace/${workspaceId}/category_mapping`,
        mapping,
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  async getProductCategories(
    accountId: number | string,
    workspaceId: number | string,
    marketingChannel: string,
    column?: string,
  ): Promise<CategoryScan> {
    const query = new URLSearchParams({
      marketing_channel: marketingChannel,
      workspace_id: String(workspaceId),
    })
    if (column) query.set('column', column)
    return (
      unwrap(
        await this.http.get<CategoryScan>(
          `/accounts/${accountId}/workspace/get_product_category_from_raw_data?${query.toString()}`,
          DEFINITIONS_REQUEST_CONFIG,
        ),
      ) ?? {}
    )
  }

  async getInventoryMapping(
    accountId: number | string,
    workspaceId: number | string,
  ): Promise<InventoryMapping> {
    const rows = unwrapEnvelope(
      await this.http.post<ApiEnvelope<InventorySnapshotRow[]>>(
        `/accounts/${accountId}/workspaces/${workspaceId}/settings/event-get-by-key/`,
        { key: 'product_stock_column_name' },
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
    return rows[0]?.value ?? {}
  }

  async saveInventoryColumn(
    accountId: number | string,
    workspaceId: number | string,
    feedSource: string,
    columnName: string[],
  ): Promise<unknown> {
    return unwrapEnvelope(
      await this.http.post(
        `/accounts/${accountId}/workspaces/${workspaceId}/settings/inventory-count/create/`,
        { feed_source: feedSource, column_name: columnName },
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  async getInventoryColumns(
    accountId: number | string,
    workspaceId: number | string,
    feedSource: string,
  ): Promise<string[]> {
    const response = unwrap(
      await this.http.get<string[] | { columns?: string[] }>(
        `/accounts/${accountId}/workspaces/${workspaceId}/settings/inventory-count/columns/?feed_source=${encodeURIComponent(feedSource)}`,
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
    return Array.isArray(response) ? response : (response.columns ?? [])
  }

  async getProductIdOptions(
    accountId: number | string,
    workspaceId: number | string,
    platform: string,
  ): Promise<string[]> {
    const rows = unwrapEnvelope(
      await this.http.get<ApiEnvelope<SelectableKeyRow[]>>(
        `/accounts/${accountId}/workspaces/${workspaceId}/settings/selectable-key/list/`,
        {
          ...DEFINITIONS_REQUEST_CONFIG,
          params: { key: true, type: 'primary_product_id', group: platform },
        },
      ),
    )
    return rows
      .flatMap((row) => (Array.isArray(row.value) ? row.value : [row.value ?? '']))
      .filter(Boolean)
  }

  async getProductIdMapping(
    accountId: number | string,
    workspaceId: number | string,
  ): Promise<Record<string, ProductIdMappingStatus>> {
    return unwrapEnvelope(
      await this.http.get<ApiEnvelope<Record<string, ProductIdMappingStatus>>>(
        `/accounts/${accountId}/workspaces/${workspaceId}/settings/product-id-mapping/`,
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  async previewFeed(payload: FeedPreviewRequest): Promise<FeedPreview> {
    return normalizePreview(
      unwrapEnvelope(
        await this.http.post<ApiEnvelope<unknown>>(
          '/preview-data/preview-data-api/',
          payload,
          DEFINITIONS_REQUEST_CONFIG,
        ),
      ),
    )
  }
}

export const productCustomizationApi = new ProductCustomizationApi()
